/**
 * Work-order rollout flag reconciliation.
 *
 * Three independent LaunchDarkly booleans (`work-order-v1-enabled`,
 * `work-order-v2-enabled`, `variable-packages-on-lot-enabled`) are not
 * independently valid. This module is the single place where the raw
 * evaluations are coerced into a usable state, so the server guards, the
 * navigation, and the UI hooks can never disagree.
 */

export type WorkOrderFlagInputs = {
  wov1: boolean;
  wov2: boolean;
  variablePackagesOnLot: boolean;
};

export type WorkOrderFlags = WorkOrderFlagInputs & {
  /** R1 fired: both engines were off, so V1 was forced back on. */
  wov1CoercedByNoEngine: boolean;
};

/**
 * Reconcile the raw flag values. Rules apply in order:
 *
 * R1  !wov1 && !wov2  ->  wov1 = true
 *     At least one work-order engine must be live, otherwise the module
 *     disappears from navigation with no way back.
 *
 * V1 refuses a VARIABLE output only at CONFIRMED -> COMPLETED, and only while
 * all three reconciled flags are on. Other states retain the legacy V1 flow.
 *
 * Reachable states and behavior after R1 reconciliation:
 *
 *   wov1  wov2  vol
 *    T     T     F    V1 VARIABLE completion allowed; container surfaces disabled
 *    T     T     T    V1 VARIABLE completion refused; migration and containers available
 *    T     F     F    Legacy V1 behavior; container surfaces disabled
 *    T     F     T    Legacy V1 behavior and containers available; known mixed-receipt gap remains
 *    F     T     F    V1 read-only archive (no creation, no record edits); containers disabled
 *    F     T     T    V1 read-only archive (no creation, no record edits); containers available
 */
export function applyWorkOrderFlagOverrides(inputs: WorkOrderFlagInputs): WorkOrderFlags {
  const wov1CoercedByNoEngine = !inputs.wov1 && !inputs.wov2;
  const wov1 = inputs.wov1 || wov1CoercedByNoEngine;

  return {
    wov1,
    wov2: inputs.wov2,
    variablePackagesOnLot: inputs.variablePackagesOnLot,
    wov1CoercedByNoEngine,
  };
}

/**
 * Navigation shape implied by the reconciled flags. Routes never change; only labels do.
 *
 * Single-engine states keep the existing `Work Orders` label so the common configuration is
 * byte-identical to pre-flag behaviour — only the two-engine state needs version suffixes to tell
 * the tabs apart, and that state already showed two distinct labels before.
 */
export type WorkOrderNavEntry = { label: string; href: "/work-orders" | "/work-orders-v2" };

export function workOrderNavEntries(flags: WorkOrderFlags): WorkOrderNavEntry[] {
  if (flags.wov1 && flags.wov2) {
    return [
      { label: "Work Orders (v1)", href: "/work-orders" },
      { label: "Work Orders (v2)", href: "/work-orders-v2" },
    ];
  }

  return [{ label: "Work Orders", href: flags.wov1 ? "/work-orders" : "/work-orders-v2" }];
}

/**
 * The V1 list is an archive reached through the V2 "Legacy Work Orders" tab exactly when V1 is off and V2 is on.
 * Takes reconciled flags, so the both-off state (V1 forced back on) never shows the tab or redirects.
 */
export function showLegacyWorkOrderTab(flags: WorkOrderFlags): boolean {
  return !flags.wov1 && flags.wov2;
}
